import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Google OAuth callback handler.
 * Reads session_id from the URL fragment (#session_id=...), exchanges it with
 * our backend for our standard JWT cookies, then routes to /home or /onboarding.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Synchronous guard to prevent StrictMode double-fire
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      const hash = window.location.hash || '';
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Google sign-in was cancelled or failed');
        navigate('/login', { replace: true });
        return;
      }

      try {
        const { data } = await axios.post(
          `${API}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        // Persist Bearer tokens for native iOS (WKWebView cookie quirks).
        try {
          if (data?.access_token) localStorage.setItem('cyb_at', data.access_token);
          if (data?.refresh_token) localStorage.setItem('cyb_rt', data.refresh_token);
        } catch { /* ignore */ }
        // Check onboarding status for fresh users
        try {
          const ob = await axios.get(`${API}/api/onboarding/progress`, { withCredentials: true });
          if (ob.data && !ob.data.completed && !ob.data.skipped) {
            window.location.href = '/onboarding';
            return;
          }
        } catch { /* not critical */ }
        // Hard nav so AuthContext rehydrates from the new cookies
        window.location.href = '/';
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not sign you in with Google');
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="app-container flex flex-col items-center justify-center min-h-screen p-6" data-testid="google-auth-callback">
      <Logo variant="full" size="lg" clickable={false} showTagline={false} />
      <div className="mt-8 flex items-center gap-3">
        <div className="w-5 h-5 border-2 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-[#A1A1AA] text-sm">Signing you in with Google…</p>
      </div>
    </div>
  );
}
